// Overhead Allocation — Run Workbench (MA-R3, Feb 2026)
//
// Workbench for POSTING allocation runs. The user picks a period,
// PREVIEWS the projected lines (dry-run), and then POSTs the run
// which persists append-only Allocation Lines.
//
// Existing Posted runs can be REVERSED (which appends negated lines)
// and REPRODUCED (which recomputes lines from the stored Frozen
// snapshot and diffs against the persisted lines — the Reproducibility
// guarantee MA-R3 hangs its hat on).
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import {
  Play, X, RefreshCw, RotateCcw, ShieldCheck, History,
  AlertCircle, Check, Undo2, Search,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";

const STATUS_CHIPS = {
  Posted:   "bg-emerald-50 text-emerald-700 border-emerald-300",
  Reversed: "bg-amber-50 text-amber-700 border-amber-300 line-through",
};

const fmtMoney = (n) => `₹${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtNum   = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

export default function AllocationRunsPage() {
  useUserDirectory();
  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ period_key: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [showRun, setShowRun] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reproduceResult, setReproduceResult] = useState(null);

  async function loadFacets() {
    try {
      const { data } = await api.get("/mca/allocation-runs/facets");
      setFacets(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load facets");
    }
  }
  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.period_key) q.set("period_key", filter.period_key);
      if (filter.status)     q.set("status", filter.status);
      const { data } = await api.get(`/mca/allocation-runs?${q.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load runs");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { load(); }, [filter.period_key, filter.status]);

  async function reverse(row) {
    const reason = window.prompt(
      `Reverse run ${row.run_number}?\n\nAn identical set of Allocation Lines will be posted with NEGATED amounts. The original lines remain intact (append-only ledger). Enter an optional reversal note:`,
      ""
    );
    if (reason === null) return;
    try {
      await api.post(`/mca/allocation-runs/${row.id}/reverse`,
        { notes: reason || `Reversal of ${row.run_number}` });
      toast.success("Reverse run posted");
      load();
      if (detail?.id === row.id) {
        const { data } = await api.get(`/mca/allocation-runs/${row.id}`);
        setDetail(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reverse failed");
    }
  }

  async function reproduce(row) {
    try {
      const { data } = await api.post(`/mca/allocation-runs/${row.id}/reproduce`);
      setReproduceResult({ run: row, ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reproduce failed");
    }
  }

  return (
    <div data-testid="allocation-runs-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Overhead Allocation · MA-R3"
        title="Allocation Run Workbench"
        subtitle="Post append-only Allocation Lines from Frozen Basis Snapshots. Every run is fully reproducible from its snapshots and can be reversed by posting a negation run (originals never mutate)."
        actions={
          <button data-testid="allocation-run-post-btn"
            onClick={() => setShowRun(true)}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Play size={14} /> New Allocation Run
          </button>
        }
      />

      <Card className="mb-4 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[180px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Period</label>
          <input type="month" value={filter.period_key}
            onChange={e => setFilter({ ...filter, period_key: e.target.value })}
            className={inputCls} data-testid="allocation-runs-period-filter" />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
          <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}
            className={inputCls} data-testid="allocation-runs-status-filter">
            <option value="">All</option>
            <option value="Posted">Posted</option>
            <option value="Reversed">Reversed</option>
          </select>
        </div>
        <button onClick={() => setFilter({ period_key: "", status: "" })}
          className="px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
          data-testid="allocation-runs-clear-filters">Clear</button>
      </Card>

      <Card>
        <div className="p-3 border-b border-slate-200 text-xs uppercase tracking-wider font-bold text-slate-500">
          {loading ? "Loading…" : `${total} run${total === 1 ? "" : "s"}`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="allocation-runs-table">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <Th>Run #</Th>
                <Th>Period</Th>
                <Th>Rules</Th>
                <Th>Lines</Th>
                <Th className="text-right">Σ Allocated</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Posted</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="9" className="p-6 text-center text-slate-400 text-sm">
                  No runs yet. Post the first Allocation Run from a Frozen Basis Snapshot.
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} onClick={() => setDetail(r)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <Td mono>{r.run_number}</Td>
                  <Td mono>{r.period_key}</Td>
                  <Td>{(r.rule_ids || []).length}</Td>
                  <Td>{r.line_count}</Td>
                  <Td className="text-right font-mono text-[12px]">{fmtMoney(r.total_allocated)}</Td>
                  <Td>
                    {r.is_reversal ? (
                      <span className="text-[11px] text-amber-700 flex items-center gap-1">
                        <Undo2 size={12} /> Reversal
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-600">Original</span>
                    )}
                  </Td>
                  <Td>
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${STATUS_CHIPS[r.status] || ""}`}
                      data-testid={`allocation-run-status-${r.run_number}`}>{r.status}</span>
                  </Td>
                  <Td>
                    <div className="text-[11px]">{new Date(r.posted_at).toLocaleDateString()}</div>
                    <div className="text-[10px] text-slate-400" title={r.posted_by || ""}>
                      <UserLabel value={r.posted_by} fallback="—" />
                    </div>
                  </Td>
                  <Td className="text-right pr-2" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => reproduce(r)}
                        className="p-1.5 hover:bg-sky-50 text-sky-700" title="Reproduce"
                        data-testid={`allocation-run-reproduce-${r.run_number}`}>
                        <ShieldCheck size={14} />
                      </button>
                      {!r.is_reversal && r.status === "Posted" && (
                        <button onClick={() => reverse(r)}
                          className="p-1.5 hover:bg-amber-50 text-amber-700" title="Reverse"
                          data-testid={`allocation-run-reverse-${r.run_number}`}>
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showRun && facets && (
        <RunWizard
          facets={facets}
          onClose={() => setShowRun(false)}
          onPosted={() => { setShowRun(false); load(); }}
        />
      )}
      {detail && (
        <RunDetail
          run={detail}
          onClose={() => setDetail(null)}
          onReverse={() => reverse(detail)}
          onReproduce={() => reproduce(detail)}
        />
      )}
      {reproduceResult && (
        <ReproduceDialog result={reproduceResult}
          onClose={() => setReproduceResult(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Run Wizard — preview → post
// ─────────────────────────────────────────────────────────────────────
function RunWizard({ facets, onClose, onPosted }) {
  const periods = facets.periods_with_frozen_snapshots || [];
  const [period, setPeriod] = useState(periods[0]?.period_key || "");
  const [notes, setNotes] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [preview, setPreview] = useState(null);

  const frozenForPeriod = useMemo(
    () => (periods.find(p => p.period_key === period)?.frozen_snapshots || []),
    [period, periods],
  );

  async function runPreview() {
    if (!period) { toast.error("Choose a period."); return; }
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/mca/allocation-runs/preview",
        { period_key: period });
      setPreview(data);
      if (data.line_count === 0) {
        toast.warning("Nothing to post — see warnings.");
      } else {
        toast.success(`Preview: ${data.line_count} line(s), ${fmtMoney(data.total_allocated)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  }

  async function post() {
    if (!preview || preview.line_count === 0) return;
    setPosting(true);
    try {
      const { data } = await api.post("/mca/allocation-runs",
        { period_key: period, notes });
      toast.success(`Posted ${data.run_number} (${data.line_count} line(s))`);
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setPosting(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end"
         data-testid="allocation-run-wizard" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Overhead Allocation · MA-R3</div>
            <div className="text-lg font-bold text-slate-800">New Allocation Run</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"
            data-testid="allocation-run-wizard-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {periods.length === 0 ? (
            <div className="bg-sky-50 border border-sky-200 p-4 text-sm text-sky-800">
              <div className="flex gap-2">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-sky-600" />
                <div>
                  <div className="font-bold text-sky-900 mb-1">No Frozen Basis Snapshots yet.</div>
                  This is expected during implementation. Once Finance reclassifies the required projects as
                  <b> Internal Cost Project</b> or <b>Corporate Project</b> and creates an Allocation Rule
                  (Overhead Allocation → Allocation Rules), Compute + Freeze at least one Basis Snapshot on the
                  Basis Snapshots page. Runs can then be posted from this workbench.
                </div>
              </div>
            </div>
          ) : (
            <>
              <Field label="Period *">
                <select value={period} onChange={e => setPeriod(e.target.value)}
                  className={inputCls} data-testid="allocation-run-period">
                  {periods.map(p => (
                    <option key={p.period_key} value={p.period_key}>
                      {p.period_key} — {p.frozen_snapshots.length} frozen snapshot(s)
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Notes (optional)">
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  className={inputCls}
                  placeholder="Free-text notes for the audit trail"
                  data-testid="allocation-run-notes" />
              </Field>

              <div className="border-t pt-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Frozen snapshots for {period}</div>
                <div className="space-y-1 text-[12px] max-h-40 overflow-y-auto">
                  {frozenForPeriod.map(s => (
                    <div key={s.id} className="flex items-center gap-3 border-b border-slate-100 py-1">
                      <span className="font-mono text-[11px]">{s.rule_code}</span>
                      <span className="flex-1 truncate">{s.rule_name}</span>
                      <span className="text-slate-500">{s.basis_type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <button onClick={runPreview} disabled={previewing || !period}
                  className="px-4 py-2 border-2 border-[#F97316] text-[#F97316] hover:bg-orange-50 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50"
                  data-testid="allocation-run-preview">
                  <RefreshCw size={12} className={previewing ? "animate-spin" : ""} />
                  {previewing ? "Computing…" : "Preview"}
                </button>
              </div>

              {preview && (
                <PreviewResult preview={preview} />
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Post writes append-only lines. Reverse later if needed.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
              data-testid="allocation-run-cancel">Cancel</button>
            <button onClick={post}
              disabled={!preview || preview.line_count === 0 || posting}
              className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-2"
              data-testid="allocation-run-post">
              <Play size={12} /> {posting ? "Posting…" : "Post Run"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewResult({ preview }) {
  return (
    <div className="border-2 border-sky-200 bg-sky-50 p-4" data-testid="allocation-run-preview-result">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-sky-700 font-bold">Preview (dry-run)</div>
        <div className="flex items-center gap-4 text-[12px]">
          <span className="font-mono">Lines: <b>{preview.line_count}</b></span>
          <span className="font-mono">Σ: <b>{fmtMoney(preview.total_allocated)}</b></span>
        </div>
      </div>
      {(preview.warnings || []).length > 0 && (
        <div className="bg-amber-100 border border-amber-300 p-2 mb-2 text-[11px] text-amber-800">
          <AlertCircle size={11} className="inline mr-1" />
          {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}
      <div className="max-h-96 overflow-y-auto">
        {(preview.rule_allocations || []).map(ra => (
          <div key={ra.rule_id} className="mb-3 border border-slate-200 bg-white">
            <div className="p-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-[11px]">
              <div>
                <span className="font-mono font-bold">{ra.rule_code}</span> · {ra.rule_name}
                <span className="text-slate-500 ml-2">({ra.basis_type})</span>
              </div>
              <div className="font-mono">Pool: <b>{fmtMoney(ra.source_indirect_pool)}</b> → Σ <b>{fmtMoney(ra.total_allocated)}</b></div>
            </div>
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-2 py-1 text-[9px] uppercase tracking-wider text-slate-500">Target</th>
                  <th className="px-2 py-1 text-right text-[9px] uppercase tracking-wider text-slate-500">Weight</th>
                  <th className="px-2 py-1 text-right text-[9px] uppercase tracking-wider text-slate-500">Allocated</th>
                </tr>
              </thead>
              <tbody>
                {ra.lines.map((line, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <span className="font-mono">{line.target_project_code}</span>
                      <span className="text-slate-500 ml-2">{line.target_project_name}</span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{fmtNum(line.weight_percent)}%</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtMoney(line.allocated_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Run Detail
// ─────────────────────────────────────────────────────────────────────
function RunDetail({ run, onClose, onReverse, onReproduce }) {
  const [lines, setLines] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("lines");

  useEffect(() => {
    (async () => {
      const [l, a] = await Promise.all([
        api.get(`/mca/allocation-runs/${run.id}/lines`),
        api.get(`/mca/allocation-runs/${run.id}/audit`),
      ]);
      setLines(l.data.rows || []);
      setAudit(a.data.rows || []);
    })().catch(e => toast.error(e.response?.data?.detail || "Detail fetch failed"));
  }, [run.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="allocation-run-detail" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Run · {run.period_key}</div>
              <div className="text-lg font-bold text-slate-800">{run.run_number}</div>
              <div className="text-[11px] text-slate-500">
                Posted by <UserLabel value={run.posted_by} fallback="—" /> on {new Date(run.posted_at).toLocaleString()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onReproduce}
                className="px-3 py-2 border-2 border-sky-600 text-sky-700 hover:bg-sky-50 text-xs uppercase tracking-wider font-bold flex items-center gap-2"
                data-testid="allocation-run-detail-reproduce">
                <ShieldCheck size={12} /> Reproduce
              </button>
              {!run.is_reversal && run.status === "Posted" && (
                <button onClick={onReverse}
                  className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs uppercase tracking-wider font-bold flex items-center gap-2"
                  data-testid="allocation-run-detail-reverse">
                  <RotateCcw size={12} /> Reverse
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            <Metric label="Status" value={run.status} />
            <Metric label="Lines" value={run.line_count} />
            <Metric label="Σ Allocated" value={fmtMoney(run.total_allocated)}
              tone={run.total_allocated >= 0 ? "neutral" : "warn"} />
            <Metric label="Engine ver." value={run.engine_version} />
          </div>
          <div className="mt-3 flex items-center gap-1 text-[11px]">
            {["lines", "audit"].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 uppercase tracking-wider font-bold ${tab === t ? "border-b-2 border-[#F97316] text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`allocation-run-detail-tab-${t}`}>{t}</button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {tab === "lines" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="allocation-run-lines-table">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <Th>Rule</Th>
                    <Th>Source</Th>
                    <Th>Target</Th>
                    <Th className="text-right">Weight %</Th>
                    <Th className="text-right">Pool</Th>
                    <Th className="text-right">Allocated</Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(ln => (
                    <tr key={ln.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-[11px]">{ln.rule_code}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-[11px]">{ln.source_project_code}</div>
                        <div className="text-[10px] text-slate-500">{ln.source_project_name}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-[11px]">{ln.target_project_code}</div>
                        <div className="text-[10px] text-slate-500">{ln.target_project_name}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtNum(ln.weight_percent)}%</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.source_indirect_pool)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${ln.allocated_amount < 0 ? "text-red-600" : ""}`}
                          data-testid={`allocation-line-amount-${ln.id}`}>{fmtMoney(ln.allocated_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-2">
              {audit.map(a => (
                <div key={a.id} className="border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <div className="font-mono">{new Date(a.timestamp).toLocaleString()}</div>
                    <div>{a.user_email}</div>
                  </div>
                  <div className="mt-1 font-bold text-slate-800 text-sm flex items-center gap-2">
                    {a.outcome === "success" ? <Check size={14} className="text-emerald-600" /> : <AlertCircle size={14} className="text-red-600" />}
                    {a.event_type}
                  </div>
                </div>
              ))}
              {audit.length === 0 && <div className="text-slate-400 text-sm text-center py-6">No audit events.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReproduceDialog({ result, onClose }) {
  const ok = result.is_byte_identical;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
         data-testid="reproduce-dialog" onClick={onClose}>
      <div className="w-full max-w-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Reproducibility Check</div>
            <div className="text-lg font-bold text-slate-800">{result.run_number}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className={`border p-4 ${ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
          data-testid="reproduce-verdict">
          <div className={`text-lg font-bold flex items-center gap-2 ${ok ? "text-emerald-700" : "text-red-700"}`}>
            {ok ? <ShieldCheck size={18} /> : <AlertCircle size={18} />}
            {ok ? "Byte-identical" : `Drift detected · ₹${result.total_drift.toLocaleString()}`}
          </div>
          <div className="text-[12px] mt-2">
            Stored lines: {result.line_count_stored} · Recomputed: {result.line_count_recomputed}
          </div>
        </div>
        {(result.diffs || []).length > 0 && (
          <div className="mt-4 border border-slate-200 p-3 max-h-72 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Per-line deltas</div>
            {result.diffs.map((d, i) => (
              <div key={i} className="text-[11px] font-mono border-b border-slate-100 py-1">
                {d.type} · {d.target_project_id.slice(0, 8)} · expected {d.expected} · stored {d.stored} · Δ {d.delta ?? "—"}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, className = "", onClick }) => (
  <td onClick={onClick} className={`px-3 py-2.5 ${mono ? "font-mono text-[12px]" : ""} ${className}`}>{children}</td>
);
const Metric = ({ label, value, tone = "neutral" }) => (
  <div className={`border p-3 ${tone === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`text-base font-bold ${tone === "warn" ? "text-amber-700" : "text-slate-800"}`}>{value}</div>
  </div>
);
const inputCls = "w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);
